/**
 * reportAdapter.ts
 *
 * Maps the stored `TitleAnalysisReport` (snake_case, as returned by
 * GET /title-analysis/results/{job_id}) onto the props the presentational
 * report components expect. Pure, no React.
 */

import type { Job } from '../../graphql/types';
import type {
  TitleAnalysisCommitmentReconciliation,
  TitleAnalysisConfidence,
  TitleAnalysisEncumbrance,
  TitleAnalysisException,
  TitleAnalysisLienPosition,
  TitleAnalysisReport,
  TitleAnalysisSeverity,
  TitleAnalysisVestingLink,
} from '../../titleAnalysis';

export interface AdaptedReport {
  grade: TitleAnalysisReport['title_grade'];
  gradeRationale: string;
  summary: string;
  analysisDate: string;
  subjectLoan: {
    expectedLienPosition: string | null;
    currentLoanHolder: string | null;
    loanAmount: number | null;
    noteDate: string | null;
  };
  lienPosition: {
    expected: number;
    derived: number | null;
    status: TitleAnalysisLienPosition['status'];
    seniorOpenEncumbranceIds: string[];
    explanation: string;
  };
  currentVestedOwner: {
    owners: string[];
    matchesSubjectMortgagor: boolean;
    notes: string;
  };
  vestingChain: Array<{
    sequence: number;
    instrumentId: string;
    conveyanceType: string;
    grantors: string[];
    grantees: string[];
    executedDate: string | null;
    recordedDate: string | null;
    consideration: number | null;
    linkToPrior: TitleAnalysisVestingLink['link_to_prior'];
  }>;
  searchCoverage: {
    searchStartDate: string | null;
    searchEffectiveDate: string | null;
    meetsMinimumPeriod: boolean;
    bringDownGapDays: number;
    gaps: string[];
  };
  exceptions: Array<{
    exceptionId: string;
    ruleCode: string;
    category: TitleAnalysisException['category'];
    severity: TitleAnalysisSeverity;
    title: string;
    description: string;
    evidence: Array<{
      instrumentId: string | null;
      fact: string;
      page: number | null;
    }>;
    confidence: TitleAnalysisConfidence;
    curativeAction: string;
    requiresHumanReview: boolean;
    assumptions: string[];
    relatedExceptionIds: string[];
  }>;
  encumbrances: Array<{
    encumbranceId: string;
    type: string;
    instrumentId: string;
    originalHolder: string;
    currentHolderOfRecord: string;
    debtors: string[];
    amount: number | null;
    recordedDate: string | null;
    status: TitleAnalysisEncumbrance['status'];
    statusBasis: string;
    assignmentChain: Array<{
      instrumentId: string;
      assignor: string;
      assignee: string;
      recordedDate: string | null;
      linkValid: boolean;
    }>;
    derivedPriorityRank: number;
    isSubjectMortgage: boolean;
  }>;
  entities: Array<{
    canonicalPartyId: string;
    canonicalName: string;
    variants: string[];
    confidence: TitleAnalysisConfidence;
    basis: string;
  }>;
  commitment: {
    provided: boolean;
    vestingMatches: TitleAnalysisCommitmentReconciliation['vesting_matches'];
    insuredMatchesSubjectLoan: TitleAnalysisCommitmentReconciliation['insured_matches_subject_loan'];
    exceptionsMissingFromScheduleB: string[];
    scheduleBItemsNotFoundInSearch: string[];
    notes: string;
  };
}

export function adaptTitleAnalysisReport(
  report: TitleAnalysisReport,
  job: Pick<
    Job,
    'expectedLienPosition' | 'currentLoanHolder' | 'subjectLoanAmount' | 'noteDate'
  >
): AdaptedReport {
  const partyNames = new Map(
    report.entity_resolution.map((e) => [e.canonical_party_id, e.canonical_name])
  );
  const names = (ids: string[]) => ids.map((id) => partyNames.get(id) ?? id);

  const lien = report.subject_lien_position;
  const coverage = report.search_coverage;
  const commitment = report.commitment_reconciliation;

  return {
    grade: report.title_grade,
    gradeRationale: report.grade_rationale,
    summary: report.summary,
    analysisDate: report.analysis_date,
    subjectLoan: {
      expectedLienPosition: job.expectedLienPosition ?? null,
      currentLoanHolder: job.currentLoanHolder ?? null,
      loanAmount: job.subjectLoanAmount ?? null,
      noteDate: job.noteDate ?? null,
    },
    lienPosition: {
      expected: lien.expected,
      derived: lien.derived,
      status: lien.status,
      seniorOpenEncumbranceIds: lien.senior_open_encumbrance_ids,
      explanation: lien.explanation,
    },
    currentVestedOwner: {
      owners: names(report.current_vested_owner.party_ids),
      matchesSubjectMortgagor: report.current_vested_owner.matches_subject_mortgagor,
      notes: report.current_vested_owner.notes,
    },
    vestingChain: report.vesting_chain.map((l) => ({
      sequence: l.sequence,
      instrumentId: l.instrument_id,
      conveyanceType: l.conveyance_type,
      grantors: names(l.grantor_party_ids),
      grantees: names(l.grantee_party_ids),
      executedDate: l.executed_date,
      recordedDate: l.recorded_date,
      consideration: l.consideration,
      linkToPrior: l.link_to_prior,
    })),
    searchCoverage: {
      searchStartDate: coverage.search_start_date,
      searchEffectiveDate: coverage.search_effective_date,
      meetsMinimumPeriod: coverage.meets_minimum_period,
      bringDownGapDays: coverage.bring_down_gap_days,
      gaps: coverage.gaps,
    },
    exceptions: report.exceptions.map((e) => ({
      exceptionId: e.exception_id,
      ruleCode: e.rule_code,
      category: e.category,
      severity: e.severity,
      title: e.title,
      description: e.description,
      evidence: e.evidence.map((ev) => ({
        instrumentId: ev.instrument_id,
        fact: ev.fact,
        page: ev.page,
      })),
      confidence: e.confidence,
      curativeAction: e.curative_action,
      requiresHumanReview: e.requires_human_review,
      assumptions: e.assumptions,
      relatedExceptionIds: e.related_exception_ids,
    })),
    encumbrances: report.encumbrances.map((e) => ({
      encumbranceId: e.encumbrance_id,
      type: e.type,
      instrumentId: e.instrument_id,
      originalHolder: e.original_holder,
      currentHolderOfRecord: e.current_holder_of_record,
      debtors: names(e.debtor_party_ids),
      amount: e.amount,
      recordedDate: e.recorded_date,
      status: e.status,
      statusBasis: e.status_basis,
      assignmentChain: e.assignment_chain.map((a) => ({
        instrumentId: a.instrument_id,
        assignor: a.assignor,
        assignee: a.assignee,
        recordedDate: a.recorded_date,
        linkValid: a.link_valid,
      })),
      derivedPriorityRank: e.derived_priority_rank,
      isSubjectMortgage: e.is_subject_mortgage,
    })),
    entities: report.entity_resolution.map((e) => ({
      canonicalPartyId: e.canonical_party_id,
      canonicalName: e.canonical_name,
      variants: e.variants,
      confidence: e.confidence,
      basis: e.basis,
    })),
    commitment: {
      provided: commitment.provided,
      vestingMatches: commitment.vesting_matches,
      insuredMatchesSubjectLoan: commitment.insured_matches_subject_loan,
      exceptionsMissingFromScheduleB: commitment.exceptions_missing_from_schedule_b,
      scheduleBItemsNotFoundInSearch: commitment.schedule_b_items_not_found_in_search,
      notes: commitment.notes,
    },
  };
}
